import { Roles } from "@outschool/auth-shared";
import { Box } from "@outschool/backpack";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassDetailsNoSectionsQuery
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  activityApprovalPath,
  activityPath,
  activitySchedulePath,
  clubManageListingPath,
  oneOnOneEnrollNewPath,
  oneOnOneEnrollPath,
  searchPath,
  sectionCharterSchoolEnrollPath,
  sectionEnrollPath
} from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useProductViewed } from "@outschool/ui-analytics";
import {
  AuthTrigger,
  EsaSessionType,
  useCurrentUserHasRole,
  useEsaWaitPeriod,
  useFundingProgramAbbr,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import {
  useRequiresMembershipForEnrollment,
  useShowCredits,
  useTrackClassPageImpression
} from "@outschool/ui-components-website";
import { Container, Space } from "@outschool/ui-legacy-component-library";
import {
  Redirect,
  Screen,
  lazyWithRetry,
  useNavigation,
  useScrollToElement
} from "@outschool/ui-utils";
import React, { useEffect } from "react";
import { Route, Switch, useLocation, useRouteMatch } from "react-router-dom";

import * as Activity from "../../../shared/Activity";
import * as User from "../../../shared/User";
import useExperiment from "../../hooks/useExperiment";
import { useRandomCategoriesByFacetQuery } from "../../hooks/useRandomCategoriesByFacet";
import { useFeatureFlag } from "../../lib/FeatureFlags";
import { isOrganizationAdminWhoCanEnroll } from "../../lib/Users";
import ActivityEnrollPage from "../../routes/ActivityEnrollPage";
import EnrollmentMeetingPurchasePage from "../../routes/EnrollmentMeetingPurchasePage";
import { useAppState } from "../../stores/AppStateProvider";
import ActivityStatusLabel from "../ActivityStatusLabel";
import { ClassScheduleRequestModal } from "../ClassScheduleRequest";
import SimpleLayout from "../layouts/SimpleLayout";
import Link from "../Link";
import ActivityMetaTags from "../sharing/ActivityMetaTags";
import Toolbar from "../Toolbar";
import useFetchEnrollableTimes from "./hooks/useFetchEnrollableTimes";
import ClassDetailsMainBody from "./mainBody/ClassDetailsMainBody";
import { MobileBelowClassDetail } from "./mobileBelowClassDetail/MobileBelowClassDetail";
import { DefaultBelowClassDetail } from "./subcomponents/DefaultBelowClassDetail";
import MobileStickyFooter from "./subcomponents/MobileStickyFooter";
import SimilarListingsMiniTiles from "./subcomponents/SimilarListingsMiniTiles";

const ActivityEmailPreview = lazyWithRetry(
  () =>
    import(
      // @ts-ignore
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityEmailPreview.jsx"
    )
);
const ActivityCharterSchoolEnrollPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityCharterSchoolEnrollPage"
    )
);
const ActivityWaitlistPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityWaitlistPage"
    )
);
const ActivityClassWalletEnrollPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityClassWalletEnrollPage"
    )
);

interface NotEnrolledViewProps {
  activity: ClassDetailsNoSectionsQuery["activity"];
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
}

export default function NotEnrolledView(props: NotEnrolledViewProps) {
  const { currentUser, userTimeZone, esaSessionType } = useSession();
  const isClassWalletSession = esaSessionType === EsaSessionType.CW;
  const { path } = useRouteMatch() ?? {};
  const isMobile = Screen.useIsMobile();
  const { t } = useTranslation(
    "client\\components\\detailPage\\NotEnrolledView"
  );
  const navigate = useNavigation();
  const showCredits = useShowCredits();
  const { activity, leader } = props;

  const isOrganizationEnrollment = isOrganizationAdminWhoCanEnroll(currentUser);
  const canManage =
    Boolean(activity) && Activity.canManage(activity, currentUser);
  const appState = useAppState();
  const canApprove = useCurrentUserHasRole(Roles.ClassApproval);
  const location = useLocation();
  const querySectionUid = location.query && location.query.sectionUid;
  const isPrepayMeetings =
    location.query?.isPrepayMeetings === "true"
      ? true
      : location.query?.isPrepayMeetings === "false"
      ? false
      : undefined;
  const isOwnerOrAdmin =
    Activity.isOwner(activity, currentUser) || User.isAdmin(currentUser);
  const isRestricted = Boolean(activity?.isRestricted) && !isOwnerOrAdmin;
  const sectionsRef = React.useRef(null);
  const otherListingsRef = React.useRef(null);
  const moreLikeThisRef = React.useRef(null);
  const viewAvailabilityRef = React.useRef(null);
  const viewTheTeacherRef = React.useRef(null);
  const { mustPurchaseMembership } = useRequiresMembershipForEnrollment();

  const [openClassScheduleRequest, setOpenClassScheduleRequest] =
    React.useState(false);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] =
    React.useState(false);

  const scrollToElement = useScrollToElement();

  const trackProductViewed = useProductViewed();
  React.useEffect(() => {
    trackProductViewed({
      // @ts-ignore TS(2322): Type '{ __typename?: "Activity" | undefined; upsel... Remove this comment to see the full error message
      activity,
      leader
    });

    const openRequestTimeModal =
      new URLSearchParams(window.location.search).get("openCSR") === "true";
    if (openRequestTimeModal && !isAvailabilityModalOpen) {
      setOpenClassScheduleRequest(openRequestTimeModal);
    }
  }, [
    activity,
    leader,
    trackProductViewed,
    navigate,
    isAvailabilityModalOpen,
    location
  ]);
  const { enterLoginFlow } = useLoginFlowContext();

  const shouldApplyEnhancedLoginFlow = useFeatureFlag(
    "enhanced-enter-login-flow"
  );
  const { variant: learnerDataFlowVariant } =
    useExperiment("CDPLearnerDataFlow");
  const shouldCollectLearnerData = learnerDataFlowVariant === "treatment";

  const generateEnrollmentUrl = React.useCallback(
    (path: string) => {
      if (!showCredits) {
        return { newUrl: path };
      }

      const params = new URLSearchParams();
      let redirectAfterPath = path;

      params.set("joinMembershipModal", "true");
      params.set("redirectAfterPath", redirectAfterPath);
      const loginUrl = `${activityPath(activity)}?${params.toString()}`;

      if (!shouldApplyEnhancedLoginFlow) {
        return {
          newUrl: loginUrl
        };
      }

      if (shouldCollectLearnerData) {
        params.set("collectLearnerData", "true");
      }
      const signUpUrl = `${activityPath(activity)}?${params.toString()}`;

      return {
        newUrlAfterSignUp: signUpUrl,
        newUrlAfterLogin: loginUrl
      };
    },
    [
      showCredits,
      shouldCollectLearnerData,
      activity,
      shouldApplyEnhancedLoginFlow
    ]
  );

  const onClickMoreLikeThis = React.useCallback(() => {
    if (isMobile && isAvailabilityModalOpen) {
      navigate(searchPath({ q: activity.title }));
    }
    scrollToElement(moreLikeThisRef.current || otherListingsRef.current);
  }, [
    navigate,
    scrollToElement,
    isMobile,
    isAvailabilityModalOpen,
    activity.title
  ]);

  const onClickViewTeacher = React.useCallback(() => {
    scrollToElement(viewTheTeacherRef?.current);
  }, [scrollToElement]);

  // tracking
  const trackClassPageImpression = useTrackClassPageImpression();
  useEffect(() => {
    trackClassPageImpression({
      activityUid: activity.uid,
      sectionUid: querySectionUid
    });
  }, [activity.uid, querySectionUid, trackClassPageImpression]);

  const onEnrollClick = React.useCallback(
    ({
      startTime,
      orgTeacherUid
    }: {
      startTime: Date;
      orgTeacherUid?: string;
    }) => {
      let path = mustPurchaseMembership
        ? oneOnOneEnrollNewPath({
            activity
          })
        : oneOnOneEnrollPath({
            activity,
            startTime,
            orgTeacherUid,
            isPrepayMeetings
          });

      enterLoginFlow({
        authTrigger: AuthTrigger.ENROLL,
        ...generateEnrollmentUrl(path)
      });
    },
    [
      activity,
      isPrepayMeetings,
      enterLoginFlow,
      generateEnrollmentUrl,
      mustPurchaseMembership
    ]
  );

  const [activityWithEnrollableTimes, setActivityWithEnrollableTimes] =
    React.useState<ActivityEnrollableTimesModal_ActivityFragment | undefined>(
      undefined
    );

  const esaState = useFundingProgramAbbr();
  const esaWaitPeriod = useEsaWaitPeriod();
  const fetchStartAfter = React.useMemo(() => {
    return !!esaState ? dayjs().add(esaWaitPeriod, "days").toDate() : undefined;
  }, [esaState, esaWaitPeriod]);

  const fetchEnrollableTimes = useFetchEnrollableTimes({
    startAfter: fetchStartAfter
  });

  React.useEffect(() => {
    if (activity.hasTeacherSchedule) {
      fetchEnrollableTimes(activity.uid).then(a => {
        setActivityWithEnrollableTimes(a);
      });
    }
  }, [activity, fetchEnrollableTimes]);

  const loadingOneOnOneActivity = !activityWithEnrollableTimes;
  const hasOneOnOneAvailabilities =
    !!activityWithEnrollableTimes?.enrollableTimes.length;

  const { data, loading: loadingRandomPopularCategories } =
    useRandomCategoriesByFacetQuery("popular", 20);
  const randomPopularCategories = React.useMemo(() => {
    if (loadingRandomPopularCategories) {
      return [];
    }
    // @ts-ignore TS(2345): Argument of type '(c: CategoryPage) => { title: st... Remove this comment to see the full error message
    return data?.randomCategoriesByFacet?.map((c: CategoryPage) => {
      return {
        title: c.nameTitleCased,
        basePath: c.basePath,
        slug: c.slug
      };
    });
  }, [data?.randomCategoriesByFacet, loadingRandomPopularCategories]);

  const showAvailableTimes = React.useMemo(() => {
    return (
      activity.isSelfPaced ||
      !(activity.isClub || activity.hasTeacherSchedule || isMobile)
    );
  }, [activity, isMobile]);

  return (
    <SimpleLayout {...props} allowCategoriesNavbar>
      {/* @ts-ignore TS(2322): Type '{ __typename?: "Section" | undefined; uid: s... Remove this comment to see the full error message */}
      <ActivityMetaTags activity={activity} />
      {currentUser && (canManage || canApprove) && (
        <Toolbar>
          <Container sx={{ marginBottom: "medium" }}>
            <Box
              flex
              sx={{
                alignItems: "center",
                justifyContent: "center",
                marginRight: "2em"
              }}
            >
              <ActivityStatusLabel activity={activity} />
              <Space x="small" />
              {canManage ? (
                <Link
                  to={
                    activity.isClub
                      ? clubManageListingPath(activity)
                      : activitySchedulePath(activity)
                  }
                >
                  {t`Manage this class`}
                </Link>
              ) : canApprove ? (
                <Link to={activityApprovalPath(activity)}>
                  {t`Approve this class`}
                </Link>
              ) : (
                ""
              )}
            </Box>
          </Container>
        </Toolbar>
      )}
      {!activity.canEnroll && !activity.isSelfPaced && (
        <Box ref={moreLikeThisRef} key="moreLikeThis">
          <SimilarListingsMiniTiles activity={activity} />
        </Box>
      )}
      {!isRestricted && (
        <>
          {isMobile && (
            <MobileStickyFooter
              activity={activity}
              isLoadingOneOnOneAvailabilities={loadingOneOnOneActivity}
              hasOneOnOneAvailabilities={hasOneOnOneAvailabilities}
              activityWithEnrollableTimes={activityWithEnrollableTimes}
              leader={leader}
              setIsAvailabilityModalOpen={setIsAvailabilityModalOpen}
            />
          )}
          {isMobile &&
            activity.upcomingAvailableSectionCount === 0 &&
            activity.upcomingSectionCount === 0 && (
              <ClassScheduleRequestModal
                activity={activity}
                user={currentUser}
                // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
                userTimeZone={userTimeZone}
                open={openClassScheduleRequest}
                setOpen={setOpenClassScheduleRequest}
              />
            )}
          <ClassDetailsMainBody
            // data
            activity={activity}
            activityWithEnrollableTimes={activityWithEnrollableTimes}
            leader={leader}
            isOrganizationEnrollment={isOrganizationEnrollment}
            isLoadingOneOnOneAvailabilities={loadingOneOnOneActivity}
            hasOneOnOneAvailabilities={hasOneOnOneAvailabilities}
            showAvailableTimes={showAvailableTimes}
            // scroll refs
            sectionsRef={sectionsRef}
            viewAvailabilityRef={viewAvailabilityRef}
            viewTheTeacherRef={viewTheTeacherRef}
            // event handlers
            onClickMoreLikeThis={onClickMoreLikeThis}
            onClickViewTeacher={onClickViewTeacher}
            onEnrollClick={onEnrollClick}
            isAvailabilityModalOpen={isAvailabilityModalOpen}
            setIsAvailabilityModalOpen={setIsAvailabilityModalOpen}
          />
          <Switch>
            <Route
              exact
              path={`${path}/enroll`}
              render={routeProps => {
                const redirectPath = appState.getEnrollPath(
                  routeProps.location
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : activity.hasTeacherSchedule ? (
                  <EnrollmentMeetingPurchasePage activityUid={activity.uid} />
                ) : isClassWalletSession ? (
                  <ActivityClassWalletEnrollPage activity={activity} />
                ) : (
                  <ActivityEnrollPage {...props} {...routeProps} />
                );
              }}
            />
            <Route
              path={`${path}/enroll`}
              render={routeProps => {
                const redirectPath = appState.getEnrollPath(
                  routeProps.location
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityEnrollPage {...props} {...routeProps} />
                );
              }}
            />
            <Route
              path={`${path}/waitlist`}
              render={routeProps => {
                const redirectPath = appState.getWaitlistPath(
                  routeProps.location
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityWaitlistPage {...props} {...routeProps} />
                );
              }}
            />
            <Route
              exact
              path={`${path}/charter-enroll`}
              render={routeProps => {
                const redirectPath = appState.getEnrollPath(
                  routeProps.location
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityCharterSchoolEnrollPage activity={activity} />
                );
              }}
            />
            <Route
              exact
              path={`${path}/email`}
              render={routeProps => {
                const redirectPath = appState.requireAuth(routeProps.location);

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  // this is a jsx file, so we need to ignore the type error
                  // @ts-ignore TS(2322): Type '{ __typename?: "Activity" | undefined; upsel... Remove this comment to see the full error message
                  <ActivityEmailPreview {...props} {...routeProps} />
                );
              }}
            />
            {!!currentUser && (
              <Route
                render={routeProps => {
                  const querySectionUid =
                    routeProps.location?.query?.sectionUid;
                  if (!!currentUser && querySectionUid && props.activity) {
                    const enrollPath = isOrganizationEnrollment
                      ? sectionCharterSchoolEnrollPath(
                          props.activity,
                          querySectionUid
                        )
                      : sectionEnrollPath(props.activity, querySectionUid);
                    return <Redirect to={enrollPath} />;
                  }
                  return null;
                }}
              />
            )}
          </Switch>
        </>
      )}
      {!isMobile && !activity.isClub && (
        <DefaultBelowClassDetail
          otherListingsRef={otherListingsRef}
          activity={activity}
          leader={leader}
          moreLikeThisRef={moreLikeThisRef}
          isRestricted={isRestricted}
          randomPopularCategories={randomPopularCategories}
        />
      )}

      {isMobile && (
        <MobileBelowClassDetail
          otherListingsRef={otherListingsRef}
          activity={activity}
          leader={leader}
        />
      )}
    </SimpleLayout>
  );
}
